import ProgressSection from "../ProgressSection";
import { useQuery } from "@tanstack/react-query";
import { supabaseExtended } from "@/integrations/supabase/extendedClient";

interface CoinInfoProps {
  coinId: string;
  tokenAddress?: string;
}

const BONDING_CURVE_TARGET = 100000;

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

const CoinInfo = ({ coinId, tokenAddress }: CoinInfoProps) => {
  const { data: progress, isLoading } = useQuery({
    queryKey: ["coin-progress", tokenAddress],
    enabled: Boolean(tokenAddress && /^0x[a-fA-F0-9]{40}$/.test(tokenAddress)),
    queryFn: async () => {
      const safeQuery = async (query: PromiseLike<any>) => {
        try { return await query; } catch { return { data: null }; }
      };
      const [coinResult, metricsResult] = await Promise.all([
        safeQuery((supabaseExtended as any).from("coin_metrics").select("*").eq("coin_address", tokenAddress).maybeSingle()),
        safeQuery((supabaseExtended as any).from("coin_metrics").select("coin_address,total_value_locked")),
      ]);
      const coin = coinResult.data || {};
      const currentMarketCap = Number(coin.market_cap ?? coin.total_value_locked ?? 0);
      const marketCaps = (metricsResult.data || [])
        .map((item: any) => Number(item.market_cap ?? item.total_value_locked ?? 0))
        .filter((value: number) => Number.isFinite(value) && value > 0);
      const leadingMarketCap = Math.max(0, ...marketCaps, currentMarketCap);
      return {
        bondingProgress: clampPercent((currentMarketCap / BONDING_CURVE_TARGET) * 100),
        kingProgress: currentMarketCap > 0 && leadingMarketCap > 0
          ? clampPercent((currentMarketCap / leadingMarketCap) * 100)
          : 0,
      };
    },
    staleTime: 30_000,
  });

  return (
    <div>
      <div className="flex items-start gap-4">
        <img 
          src="/placeholder.svg" 
          alt="Coin logo" 
          className="w-16 h-16 rounded-lg"
        />
        <div>
          <h3 className="text-lg font-semibold">{coinId}</h3>
          <p className="text-sm text-muted-foreground">
            It's our job to help save this coin! Sign the petition!
          </p>
        </div>
      </div>
      <ProgressSection
        bondingProgress={progress?.bondingProgress ?? 0}
        kingProgress={progress?.kingProgress ?? 0}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CoinInfo;
